import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createProfileQueryKey } from '@/utils/queryKeys';
import type {
  ScheduleOrganizationSeatPackRemovalMutation,
  ScheduleOrganizationSeatPackRemovalMutationVariables,
} from './useScheduleSeatPackRemoval.generated';

const query = gql`
  mutation ScheduleOrganizationSeatPackRemoval(
    $input: ScheduleOrganizationSeatPackRemovalInput!
  ) {
    scheduleOrganizationSeatPackRemoval(input: $input)
  }
`;

// Schedules a seat-pack decrease for the end of the billing period. Blocked
// server-side while the org's member count exceeds the reduced limit.
export const useScheduleSeatPackRemoval = (organizationId: string) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    ScheduleOrganizationSeatPackRemovalMutation,
    QueryError,
    ScheduleOrganizationSeatPackRemovalMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        ScheduleOrganizationSeatPackRemovalMutation,
        ScheduleOrganizationSeatPackRemovalMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createProfileQueryKey() });
    },
  });

  const scheduleSeatPackRemoval = (
    packs: number,
    options?: MutateOptions<
      ScheduleOrganizationSeatPackRemovalMutation,
      QueryError,
      ScheduleOrganizationSeatPackRemovalMutationVariables
    >
  ) => {
    return mutate({ input: { organizationId, packs } }, options);
  };

  return { scheduleSeatPackRemoval, isPending };
};
